import type { CuratedTriadHit } from '../types';
import { normalizedTriadKey } from '../types';

export interface CuratedTriad {
  id: string;
  slugs: [string, string, string];
  label: string;
  description: string;
  pattern: 'progressive' | 'regressive' | 'cyclical' | 'conflictual' | 'harmonic';
  tags: string[];
  intensity: 1 | 2 | 3 | 4 | 5;
}

export const CURATED_TRIADS: CuratedTriad[] = [
  {
    id: 'triad-matto-morte-mondo',
    slugs: ['il-matto', 'la-morte', 'il-mondo'],
    label: 'Soglia, recisione, compimento',
    description: 'Il salto iniziale attraversa una perdita necessaria e arriva a una forma piena.',
    pattern: 'progressive',
    tags: ['threshold', 'ending', 'completion'],
    intensity: 5,
  },
  {
    id: 'triad-luna-diavolo-torre',
    slugs: ['la-luna', 'il-diavolo', 'la-torre'],
    label: 'Discesa dell’illusione',
    description: 'Paura, legame e crollo si dispongono come un vettore netto di smascheramento.',
    pattern: 'conflictual',
    tags: ['shadow', 'illusion', 'collapse'],
    intensity: 5,
  },
  {
    id: 'triad-papessa-eremita-stella',
    slugs: ['la-papessa', 'l-eremita', 'la-stella'],
    label: 'Studio interiore che orienta',
    description: 'Silenzio, ritiro e fiducia si saldano in una guida sottile ma affidabile.',
    pattern: 'harmonic',
    tags: ['study', 'solitude', 'guidance'],
    intensity: 4,
  },
  {
    id: 'triad-imperatore-giustizia-giudizio',
    slugs: ['l-imperatore', 'la-giustizia', 'il-giudizio'],
    label: 'Autorità chiamata a rispondere',
    description: 'La struttura viene pesata e poi chiamata a trasformarsi in atto pubblico.',
    pattern: 'progressive',
    tags: ['authority', 'truth', 'calling'],
    intensity: 4,
  },
  {
    id: 'triad-amanti-diavolo-temperanza',
    slugs: ['gli-amanti', 'il-diavolo', 'la-temperanza'],
    label: 'Vincolo, tentazione, ricalibrazione',
    description: 'Il legame entra in crisi solo per trovare un equilibrio più cosciente.',
    pattern: 'conflictual',
    tags: ['bond', 'temptation', 'alchemy'],
    intensity: 4,
  },
  {
    id: 'triad-torre-stella-sole',
    slugs: ['la-torre', 'la-stella', 'il-sole'],
    label: 'Dalla frattura alla luce piena',
    description: 'La crisi rompe, la stella riallinea e il sole rende finalmente evidente il nuovo stato.',
    pattern: 'progressive',
    tags: ['collapse', 'recovery', 'clarity'],
    intensity: 5,
  },
  {
    id: 'triad-appeso-morte-temperanza',
    slugs: ['l-appeso', 'la-morte', 'la-temperanza'],
    label: 'Sacrificio, taglio, trasmutazione',
    description: 'La resa produce una fine utile solo se viene seguita da una lenta composizione.',
    pattern: 'progressive',
    tags: ['surrender', 'ending', 'alchemy'],
    intensity: 4,
  },
  {
    id: 'triad-papessa-luna-diavolo',
    slugs: ['la-papessa', 'la-luna', 'il-diavolo'],
    label: 'Segreto che scivola in ossessione',
    description: 'L’intuizione si fa torbida e rischia di diventare cattura.',
    pattern: 'regressive',
    tags: ['intuition', 'illusion', 'shadow'],
    intensity: 5,
  },
  {
    id: 'triad-ruota-carro-giudizio',
    slugs: ['la-ruota-della-fortuna', 'il-carro', 'il-giudizio'],
    label: 'Svolta che pretende decisione',
    description: 'Ciò che gira si accelera e infine chiede una risposta irrevocabile.',
    pattern: 'progressive',
    tags: ['fate', 'movement', 'calling'],
    intensity: 4,
  },
  {
    id: 'triad-imperatrice-stella-mondo',
    slugs: ['l-imperatrice', 'la-stella', 'il-mondo'],
    label: 'Nutrire, guarire, compiere',
    description: 'La crescita diventa piena quando la cura trova una forma ampia e vivibile.',
    pattern: 'harmonic',
    tags: ['fertility', 'healing', 'completion'],
    intensity: 4,
  },
  {
    id: 'triad-diavolo-torre-giudizio',
    slugs: ['il-diavolo', 'la-torre', 'il-giudizio'],
    label: 'Vincolo, rottura, risveglio',
    description: 'La catena si spezza e costringe a una chiamata che non può più essere evitata.',
    pattern: 'progressive',
    tags: ['bondage', 'collapse', 'awakening'],
    intensity: 5,
  },
  {
    id: 'triad-matto-sole-mondo',
    slugs: ['il-matto', 'il-sole', 'il-mondo'],
    label: 'Salto, esposizione, compimento',
    description: 'L’innesco ingenuo diventa chiarezza e poi stabilità integrata.',
    pattern: 'progressive',
    tags: ['beginning', 'clarity', 'completion'],
    intensity: 4,
  },
  {
    id: 'triad-eremita-papessa-temperanza',
    slugs: ['l-eremita', 'la-papessa', 'la-temperanza'],
    label: 'Disciplina del silenzio',
    description: 'Ritiro, intuizione e alchimia lavorano come una sola corrente contenuta.',
    pattern: 'harmonic',
    tags: ['silence', 'study', 'alchemy'],
    intensity: 3,
  },
  {
    id: 'triad-forza-diavolo-morte',
    slugs: ['la-forza', 'il-diavolo', 'la-morte'],
    label: 'Istinto, legame, recisione',
    description: 'Ciò che è stato domato male chiede prima confronto e poi taglio netto.',
    pattern: 'conflictual',
    tags: ['instinct', 'shadow', 'ending'],
    intensity: 5,
  },
  {
    id: 'triad-giustizia-mondo-stella',
    slugs: ['la-giustizia', 'il-mondo', 'la-stella'],
    label: 'Bilancio, compimento, respiro',
    description: 'Una chiusura giusta permette finalmente una distensione autentica.',
    pattern: 'harmonic',
    tags: ['balance', 'completion', 'recovery'],
    intensity: 3,
  },
];

const TRIAD_LOOKUP = new Map<string, CuratedTriad>(
  CURATED_TRIADS.map((triad) => [normalizedTriadKey(triad.slugs), triad]),
);

export const findCuratedTriad = (slugs: readonly string[]): CuratedTriad | undefined =>
  TRIAD_LOOKUP.get(normalizedTriadKey(slugs));

export const toCuratedTriadHit = (triad: CuratedTriad): CuratedTriadHit => ({
  curatedId: triad.id,
  label: triad.label,
  description: triad.description,
  tags: triad.tags,
  pattern: triad.pattern,
  intensity: triad.intensity,
  bonus: triad.intensity * 3.5,
});
